import React, { useState, useEffect } from 'react';
import {
  Typography, Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Paper, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel,
  Select, MenuItem, Alert, TablePagination
} from '@mui/material';
import { Add, Edit, Delete, Download } from '@mui/icons-material';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const TablesPage = () => {
  const [tables, setTables] = useState([]);
  const [filteredTables, setFilteredTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tableDialog, setTableDialog] = useState({ open: false, table: null, mode: 'add' });

  // Filters
  const [locationFilter, setLocationFilter] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    fetchTables();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [tables, locationFilter, availabilityFilter]);

  const fetchTables = async () => {
    try {
      const res = await api.get('/tables');
      setTables(res.data);
    } catch (err) {
      setError('Failed to fetch tables');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...tables];
    if (locationFilter) {
      filtered = filtered.filter(t => t.location === locationFilter);
    }
    if (availabilityFilter) {
      filtered = filtered.filter(t => t.isAvailable === (availabilityFilter === 'available'));
    }
    setFilteredTables(filtered);
  };

  const handleTableSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const tableData = {
      tableNumber: formData.get('tableNumber'),
      capacity: parseInt(formData.get('capacity')),
      location: formData.get('location'),
      description: formData.get('description'),
      price: parseFloat(formData.get('price') || 0),
    };

    try {
      if (tableDialog.mode === 'add') {
        await api.post('/tables', tableData);
      } else {
        await api.put(`/tables/${tableDialog.table._id}`, tableData);
      }
      fetchTables();
      setTableDialog({ open: false, table: null, mode: 'add' });
    } catch {
      setError('Failed to save table');
    }
  };

  const handleDeleteTable = async (id) => {
    if (window.confirm('Delete this table?')) {
      try {
        await api.delete(`/tables/${id}`);
        fetchTables();
      } catch {
        setError('Failed to delete table');
      }
    }
  };

  const handleExportCSV = () => {
    const rows = filteredTables.map(t => ({
      TableNumber: t.tableNumber,
      Capacity: t.capacity,
      Location: t.location,
      Status: t.isAvailable ? 'Available' : 'Unavailable',
      Price: t.price,
    }));

    const csv = [
      Object.keys(rows[0]).join(','),
      ...rows.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'tables.csv';
    a.click();
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h5" mb={2}>Manage Tables</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" gap={2}>
            <FormControl size="small" style={{width:"140px"}}>
              <InputLabel>Location</InputLabel>
              <Select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)} label="Location">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="indoor">Indoor</MenuItem>
                <MenuItem value="outdoor">Outdoor</MenuItem>
                <MenuItem value="private">Private</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small"  style={{width:"140px"}}>
              <InputLabel>Status</InputLabel>
              <Select value={availabilityFilter} onChange={(e) => setAvailabilityFilter(e.target.value)} label="Status">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="unavailable">Unavailable</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" gap={2}>
            <Button variant="outlined" startIcon={<Download />} onClick={handleExportCSV}>
              Export CSV
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => setTableDialog({ open: true, table: null, mode: 'add' })}>
              Add Table
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Table Number</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Status</TableCell>
                {/* <TableCell>Price</TableCell> */}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTables.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(table => (
                <TableRow key={table._id}>
                  <TableCell>{table.tableNumber}</TableCell>
                  <TableCell>{table.capacity}</TableCell>
                  <TableCell>{table.location}</TableCell>
                  <TableCell>
                    <Chip label={table.isAvailable ? 'Available' : 'Unavailable'} color={table.isAvailable ? 'success' : 'error'} size="small" />
                  </TableCell>
                  {/* <TableCell>${table.price}</TableCell> */}
                  <TableCell>
                    <Button size="small" startIcon={<Edit />} onClick={() => setTableDialog({ open: true, table, mode: 'edit' })}>
                      Edit
                    </Button>
                    <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleDeleteTable(table._id)}>
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredTables.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20]}
          />
        </TableContainer>

        {/* Add/Edit Dialog */}
        <Dialog open={tableDialog.open} onClose={() => setTableDialog({ open: false, table: null, mode: 'add' })}>
          <DialogTitle>{tableDialog.mode === 'add' ? 'Add Table' : 'Edit Table'}</DialogTitle>
          <Box component="form" onSubmit={handleTableSubmit}>
            <DialogContent>
              <TextField fullWidth name="tableNumber" label="Table Number" defaultValue={tableDialog.table?.tableNumber || ''} margin="normal" required />
              <TextField fullWidth name="capacity" label="Capacity" type="number" defaultValue={tableDialog.table?.capacity || ''} margin="normal" required />
              <FormControl fullWidth margin="normal">
                <InputLabel>Location</InputLabel>
                <Select name="location" defaultValue={tableDialog.table?.location || 'indoor'}>
                  <MenuItem value="indoor">Indoor</MenuItem>
                  <MenuItem value="outdoor">Outdoor</MenuItem>
                  <MenuItem value="private">Private</MenuItem>
                </Select>
              </FormControl>
              <TextField fullWidth name="description" label="Description" multiline rows={2} defaultValue={tableDialog.table?.description || ''} margin="normal" />
              <TextField    sx={{ display: 'none' }}   // hides completely
 fullWidth name="price" label="Price" type="number" defaultValue={tableDialog.table?.price || 0} margin="normal" />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTableDialog({ open: false, table: null, mode: 'add' })}>Cancel</Button>
              <Button type="submit" variant="contained">{tableDialog.mode === 'add' ? 'Add' : 'Update'}</Button>
            </DialogActions>
          </Box>
        </Dialog>
      </Box>
    </Box>
  );
};

export default TablesPage;
